<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { debounce } from '@chatwoot/utils';
import { useDealsStore } from 'dashboard/stores/deals';

import DealsListLayout from 'dashboard/components-next/CRM/DealsListLayout.vue';
import DealsCard from 'dashboard/components-next/CRM/DealsCard/DealsCard.vue';
import DealsEmptyState from 'dashboard/components-next/CRM/EmptyState/DealsEmptyState.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import CreateDealDialog from 'dashboard/components-next/CRM/DealsForm/CreateDealDialog.vue';

const DEBOUNCE_DELAY = 300;

const dealsStore = useDealsStore();

const route = useRoute();
const router = useRouter();
const { t } = useI18n();

const deals = computed(() => dealsStore.getDealsList);
const meta = computed(() => dealsStore.getMeta);
const uiFlags = computed(() => dealsStore.getUIFlags);
const currentFilters = computed(() => dealsStore.getCurrentFilters);

const searchQuery = computed(() => route.query?.search || '');
const sortQuery = computed(() => route.query?.sort || 'updated_at_desc');
const searchValue = ref(searchQuery.value);
const pageNumber = computed(() => Number(route.query?.page) || 1);

const isFetchingList = computed(() => uiFlags.value.fetchingList);
const currentPage = computed(() => Number(meta.value?.currentPage));
const totalItems = computed(() => meta.value?.count);

const hasDeals = computed(() => deals.value.length > 0);
const isDealIndexView = computed(
  () => route.name === 'crm_deals_index' && pageNumber.value === 1
);

const showEmptyStateLayout = computed(() => {
  return !searchQuery.value && !hasDeals.value && isDealIndexView.value;
});

const showEmptyText = computed(() => {
  return (searchQuery.value || !isDealIndexView.value) && !hasDeals.value;
});

const updateURLParams = (page, search = '', sort = '') => {
  const query = {
    ...route.query,
    page: page.toString(),
  };

  if (search) {
    query.search = search;
  } else {
    delete query.search;
  }

  if (sort) {
    query.sort = sort;
  } else {
    delete query.sort;
  }

  router.replace({ query });
};

const fetchDeals = async (page, search, sort) => {
  const currentPage = page ?? pageNumber.value;
  const currentSearch = search ?? searchQuery.value;
  const currentSort = sort ?? sortQuery.value;

  if (page !== undefined || search !== undefined || sort !== undefined) {
    updateURLParams(currentPage, currentSearch, currentSort);
  }

  await dealsStore.get({
    page: currentPage,
    search: currentSearch,
    sort: currentSort,
  });
};

const onSearch = debounce(query => {
  searchValue.value = query;
  fetchDeals(1, query, sortQuery.value);
}, DEBOUNCE_DELAY);

const onSortChange = sort => {
  fetchDeals(pageNumber.value, searchQuery.value, sort);
};

const onPageChange = page => {
  fetchDeals(page, searchValue.value, sortQuery.value);
};

const handleShowDeal = dealId => {
  router.push(`/app/accounts/${route.params.accountId}/crm/deals/${dealId}`);
};

const createDealDialogRef = ref(null);

const handleAddDeal = () => {
  createDealDialogRef.value?.open();
};

const handleCreateDeal = async dealData => {
  try {
    await dealsStore.create(dealData);
    createDealDialogRef.value?.onSuccess();
    useAlert(t('DEALS.CREATE_SUCCESS'));
    fetchDeals();
  } catch (error) {
    useAlert(t('DEALS.CREATE_ERROR'));
  }
};

const emptyStateMessage = computed(() => {
  if (!searchQuery.value) {
    return t('DEALS.EMPTY_STATE.LIST_EMPTY_STATE_TITLE');
  }
  return t('DEALS.EMPTY_STATE.SEARCH_EMPTY_STATE_TITLE');
});

onMounted(() => {
  searchValue.value = searchQuery.value;
  fetchDeals();
});
</script>

<template>
  <div
    class="flex flex-col justify-between flex-1 h-full m-0 overflow-auto bg-n-background"
  >
    <DealsListLayout
      :search-value="searchValue"
      :header-title="$t('DEALS.TITLE')"
      :current-page="pageNumber"
      :total-items="Number(meta.count || 0)"
      :active-sort="sortQuery"
      :is-fetching-list="isFetchingList"
      :show-pagination-footer="!!deals.length"
      @update:current-page="onPageChange"
      @update:sort="onSortChange"
      @search="onSearch"
      @add="handleAddDeal"
    >
      <div
        v-if="isFetchingList"
        class="flex items-center justify-center py-10 text-n-slate-11"
      >
        <Spinner />
      </div>

      <template v-else>
        <DealsEmptyState
          v-if="showEmptyStateLayout"
          class="pt-14"
          :title="$t('DEALS.EMPTY_STATE.TITLE')"
          :subtitle="$t('DEALS.EMPTY_STATE.SUBTITLE')"
          :button-label="$t('DEALS.EMPTY_STATE.BUTTON_LABEL')"
          @create="handleAddDeal"
        />

        <div
          v-else-if="showEmptyText"
          class="flex items-center justify-center py-10"
        >
          <span class="text-base text-n-slate-11">
            {{ emptyStateMessage }}
          </span>
        </div>

        <div v-else class="flex flex-col gap-4 px-6 pt-4 pb-6">
          <DealsCard
            v-for="deal in deals"
            :key="deal.id"
            :id="deal.id"
            :title="deal.title"
            :value="deal.value"
            :currency="deal.currency"
            :stage="deal.stage"
            :probability="deal.probability"
            :company-name="deal.company?.name"
            :user-name="deal.user?.name"
            :updated-at="deal.updatedAt"
            @show-deal="handleShowDeal"
          />
        </div>
      </template>
    </DealsListLayout>
    
    <CreateDealDialog
      ref="createDealDialogRef"
      @create="handleCreateDeal"
    />
  </div>
</template>
